import AddIcon from "@mui/icons-material/Add";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MinusIcon from "@mui/icons-material/Remove";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import { CanceledError } from "axios";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import AuthEmailAPIService, { AuthResponse } from "users/_api/auth-email-api-service";
import UserInformation from "users/information/user-information";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import SuperUserAPIService, { SuperUserAPIServiceError } from "users/super-users/_api/super-user-api-service";

const RowActionsContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
}));

type UsersAdminProps = {
  onProfileView: (type: string, id?: string) => void;
};

  const columnHelper = createMRTColumnHelper<UserInformation>();

export default function UsersAdmin(props: Readonly<UsersAdminProps>) {
  const { onProfileView } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [data, setData] = React.useState<UserInformation[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const confirm = useConfirmDialog();
  const session = useSession();
  const navigate = useNavigate();

  const fetchData = useCallback(async (abortController?: AbortController) => {
    setIsRefetching(Boolean(data.length));

    try {
      const apiService = new SuperUserAPIService(session);
      const request = new SearchUsersAPIRequest(
        query ?? "",
        pagination.pageIndex,
        pagination.pageSize,
        sorting.length > 0 ? sorting[0] : undefined,
      );
      const pagedUserResponse = await apiService.searchUsers(request, abortController);

      setData(pagedUserResponse.data);
      setRowCount(pagedUserResponse.totalElements);
      setPagination({
        pageIndex: pagedUserResponse.pageIndex,
        pageSize: pagedUserResponse.pageSize,
      });
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setWasErrorLoading(true);
      return;
    }
    setIsLoading(false);
    setIsRefetching(false);
    setWasErrorLoading(false);
  }, [data.length, session, query, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    let abortController = new AbortController();
    void fetchData(abortController);
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [columnFilters, query, pagination.pageIndex, pagination.pageSize, sorting, fetchData]);

  async function registerSuperUser(_event: React.MouseEvent<HTMLButtonElement, MouseEvent>, user: UserInformation) {
    if (!user) return;
    const response = await confirm({
      title: `Register ${user.email!.value} as a superuser?`,
      message: "Are you sure you want to register this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.register(user.id);
      await fetchData();
    } catch (err) {
      if (err instanceof SuperUserAPIServiceError) {
        console.error("Couldn't register superuser");
        enqueueSnackbar("Couldn't register superuser", { variant: "error" });
      } else throw err;
    }
  }

  async function unRegisterSuperUser(_event: React.MouseEvent<HTMLButtonElement, MouseEvent>, user: UserInformation) {
    const response = await confirm({
      title: `Unregister ${user.email!.value} as a superuser?`,
      message: "Are you sure you want to unregister this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      await superUserService.unregister(user.id);
      await fetchData();
    } catch (err) {
      if (err instanceof SuperUserAPIServiceError) {
        console.error("Couldn't unregister superuser");
        enqueueSnackbar("Couldn't unregister superuser", { variant: "error" });
      } else throw err;
    }
  }

  async function resetUserPassword(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    data: UserInformation | UserInformation[],
  ) {
    let user: UserInformation;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const response = await confirm({
      title: `Reset ${user.email}'s password?`,
      message: "Are you sure you want to reset this user's password?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await AuthEmailAPIService.requestPasswordReset(user.email!);
      enqueueSnackbar("Sent password reset email to user", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't reset user password", { variant: "error" });
    }
  }

  async function loginAsUser(_event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: Guid | Guid[]) {
    let userId: Guid;
    if (Array.isArray(data)) {
      userId = data[0];
    } else {
      userId = data;
    }
    const response = await confirm({
      title: `Login as ${userId.value}?`,
      message: "Are you sure you want to login as this user?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const superUserService = new SuperUserAPIService(session);
      setIsLoading(true);
      const authResponse: AuthResponse = await superUserService.loginAsUser(userId);
      session.initializeFromAuthResponse(authResponse);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't login as user", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  const renderCompanyColumn = useCallback((user: UserInformation) => {
    if (!user.companyId) return null;
    return (
      <Link
        style={{
          color: "#000",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={() => {
          onProfileView("company", user?.companyId?.value);
        }}
      >
        {user.companyName}
      </Link>
    );
  }, [onProfileView]);

  const renderEmailCell = useCallback((email: string) => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "500",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </Typography>
    );
  }, []);

  const renderClientVendorCell = useCallback((isChecked: boolean | undefined) => {
    return (
      <Checkbox
        checked={isChecked ?? false}
        indeterminate={isChecked === undefined}
        disabled
        size={"small"}
        sx={{
          padding: 0,
          "&.Mui-checked": {
            color: "orange",
          },
        }}
      />
    );
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor((user) => user.email!.value, {
      header: "Email",
      id: "email",
      Cell: ({ row }) => renderEmailCell(row.original.email!.value),
    }),
    columnHelper.accessor((user) => user.name.firstName, {
      header: "First Name",
      id: "firstName",
    }),
    columnHelper.accessor((user) => user.name.lastName, {
      header: "Last Name",
      id: "lastName",
    }),
    columnHelper.accessor(renderCompanyColumn, {
      header: "Company",
      id: "companyName",
    }),
    columnHelper.accessor((user) => user.isVendor, {
      header: "Vendor?",
      id: "isVendor",
      Cell: ({ cell }) => renderClientVendorCell(cell.getValue()),
    }),
    columnHelper.accessor((user) => user.isClient, {
      header: "Client?",
      id: "isClient",
      Cell: ({ cell }) => renderClientVendorCell(cell.getValue()),
    }),
  ], [renderCompanyColumn, renderEmailCell, renderClientVendorCell]);

  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns,
    data,
    enableRowSelection: true,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      showGlobalFilter: true,
    },
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <MinusIcon /> : <AddIcon />,
    }),
    renderRowActions: ({ row, table: _ }) => (
      <RowActionsContainer>
        <Tooltip title={row.original.isSuperUser ? "Unregister As Superuser" : "Register As Superuser"}>
          <IconButton
            color={row.original.isSuperUser ? "error" : "primary"}
            onClick={(event) => {
              if (row.original.isSuperUser) {
                void unRegisterSuperUser(event, row.original);
              } else {
                void registerSuperUser(event, row.original);
              }
            }}
          >
            <SecurityIcon style={{ color: row.original.isSuperUser ? "error" : "primary" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Force Password Reset">
          <IconButton color="primary" onClick={(event) => resetUserPassword(event, row.original)}>
            <VpnKeyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Login As User">
          <IconButton
            color="secondary"
            onClick={(event) => {
              void loginAsUser(event, row.original.id);
            }}
          >
            <LockOpenIcon />
          </IconButton>
        </Tooltip>
      </RowActionsContainer>
    ),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  }

  return <MRTNoMemo<UserInformation> options={tableOptions} />;
}
